import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";
import { Smartphone, Search, Zap, CheckCircle2, XCircle, Printer, Bluetooth, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PosLockedOverlay } from "@/components/layout/PosShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatBs, formatDate } from "@/lib/format";
import { getPrinterSettings } from "@/lib/printerConfig";
import { printRechargeViaBluetooth, isWebBluetoothAvailable } from "@/lib/bluetoothPrint";
import { printRechargeViaSerial, isWebSerialAvailable } from "@/lib/serialPrint";
import { systemPrint, isDesktopApp, isDesktopRawActive, printRechargeViaDesktop } from "@/lib/desktopPrint";

export default function PosRechargesPage() {
  const { user } = useAuth();
  const gate = useOutletContext() || {};
  const locked = !!gate.suspended;
  const [providerId, setProviderId] = useState("");
  const [cuenta, setCuenta] = useState("");
  const [cedula, setCedula] = useState("");
  const [tipo, setTipo] = useState("contrato");
  const [monto, setMonto] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [consultaResult, setConsultaResult] = useState(null);
  const [comprobante, setComprobante] = useState(null);
  const [paperSize, setPaperSize] = useState(() => getPrinterSettings().paper_size);
  const [btPrinting, setBtPrinting] = useState(false);
  const autoPrintedRef = useRef(null);

  const { data: providersData } = useQuery({
    queryKey: ["ekiipago-providers"],
    queryFn: async () => (await api.get("/ekiipago/providers")).data,
  });
  const providers = providersData?.providers || [];
  const provider = useMemo(
    () => providers.find((p) => String(p.id) === String(providerId)),
    [providers, providerId]
  );

  const consultaMut = useMutation({
    mutationFn: async () =>
      (await api.post("/ekiipago/consulta", {
        provider_id: Number(providerId),
        cuenta,
        tipo: provider?.requires_tipo ? tipo : undefined,
      })).data,
    onSuccess: (d) => {
      setConsultaResult(d);
      if (d.ok) toast.success("Consulta exitosa");
      else toast.error(d.message || "Sin datos");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error en la consulta"),
  });

  const recargaMut = useMutation({
    mutationFn: async () =>
      (await api.post("/ekiipago/recarga", {
        provider_id: Number(providerId),
        cuenta,
        monto: Number(monto),
        cedula: provider?.requires_cedula ? cedula : undefined,
        tipo: provider?.requires_tipo ? tipo : undefined,
        customer_name: customerName || undefined,
        channel: "pos",
      })).data,
    onSuccess: (d) => {
      if (d.status === "approved") {
        setComprobante(d);
        toast.success("Recarga aprobada");
        setMonto("");
      } else {
        toast.error(d.message || `Recarga rechazada (${d.error_code || "error"})`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo procesar la recarga"),
  });

  const canConsultar = !locked && provider?.supports_consulta && cuenta.trim().length > 2;
  const canRecargar =
    !locked &&
    provider &&
    cuenta.trim().length > 2 &&
    Number(monto) > 0 &&
    (!provider.requires_cedula || cedula.trim().length > 3);

  const resetForm = () => {
    setCuenta("");
    setCedula("");
    setMonto("");
    setCustomerName("");
    setConsultaResult(null);
    setComprobante(null);
    autoPrintedRef.current = null;
  };

  const printBluetooth = async () => {
    if (!comprobante) return;
    setBtPrinting(true);
    const s = getPrinterSettings();
    const res = await printRechargeViaBluetooth(comprobante, {
      operatorName: user?.full_name || "",
      paperSize,
      feedLines: s.feed_lines,
      cutPaper: s.cut_paper,
    });
    setBtPrinting(false);
    if (res.ok) toast.success("Comprobante enviado a la impresora Bluetooth");
    else toast.error(res.error || "No se pudo imprimir por Bluetooth");
  };
  const printSerial = async () => {
    if (!comprobante) return;
    const s = getPrinterSettings();
    const ctx = { operatorName: user?.full_name || "", paperSize, feedLines: s.feed_lines, cutPaper: s.cut_paper, baudRate: s.serial_baud };
    const res = isDesktopRawActive() ? await printRechargeViaDesktop(comprobante, ctx) : await printRechargeViaSerial(comprobante, ctx);
    if (res.ok) toast.success("Comprobante enviado a la impresora directa");
    else toast.error(res.error || "No se pudo imprimir en la impresora directa");
  };
  // Botón Imprimir: ESC/POS directo si el método es serie; si no, navegador.
  const printManual = () => (isDesktopRawActive() || (getPrinterSettings().print_method === "serial" && isWebSerialAvailable()) ? printSerial() : systemPrint());

  // Auto-imprimir al aprobar la recarga si está configurado (una vez por comprobante).
  useEffect(() => {
    if (!comprobante || comprobante.status !== "approved") return;
    if (autoPrintedRef.current === comprobante.id) return;
    autoPrintedRef.current = comprobante.id;
    const s = getPrinterSettings();
    if (!s.auto_print_on_sell) return;
    if (s.print_method === "bluetooth" && isWebBluetoothAvailable()) {
      printBluetooth();
    } else if (isDesktopRawActive() || (s.print_method === "serial" && isWebSerialAvailable())) {
      printSerial();
    } else {
      setTimeout(() => systemPrint(), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comprobante]);

  return (
    <div className="relative px-4 sm:px-6 py-5 max-w-3xl mx-auto space-y-5" data-testid="pos-recharges-page">
      <PosLockedOverlay />
      <fieldset disabled={locked} aria-disabled={locked} className="space-y-5 min-w-0" data-testid="pos-recharges-fieldset">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/15 grid place-items-center">
          <Smartphone className="text-primary" size={20} />
        </div>
        <div>
          <h1 className="text-xl font-bold leading-tight">Recargas / Servicios</h1>
          <p className="text-sm text-muted-foreground">Telefonía, TV y servicios del hogar</p>
        </div>
      </div>

      <Card className="p-5 space-y-4">
        <div className="space-y-1.5">
          <Label>Proveedor</Label>
          <Select
            value={providerId}
            onValueChange={(v) => {
              setProviderId(v);
              setConsultaResult(null);
              setComprobante(null);
            }}
          >
            <SelectTrigger data-testid="recharge-provider-select">
              <SelectValue placeholder="Selecciona una operadora" />
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p.id} value={String(p.id)} data-testid={`recharge-provider-${p.id}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {provider && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{provider.cuenta_label}</Label>
                <Input
                  value={cuenta}
                  onChange={(e) => setCuenta(e.target.value)}
                  placeholder={provider.cuenta_label}
                  data-testid="recharge-cuenta-input"
                />
              </div>
              {provider.requires_cedula && (
                <div className="space-y-1.5">
                  <Label>Cédula / RIF del cliente</Label>
                  <Input
                    value={cedula}
                    onChange={(e) => setCedula(e.target.value)}
                    placeholder="Solo números"
                    data-testid="recharge-cedula-input"
                  />
                </div>
              )}
              {provider.requires_tipo && (
                <div className="space-y-1.5">
                  <Label>Tipo de identificador</Label>
                  <Select value={tipo} onValueChange={setTipo}>
                    <SelectTrigger data-testid="recharge-tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contrato">Contrato</SelectItem>
                      <SelectItem value="documento">Documento (RIF/Cédula)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {provider.supports_consulta && (
              <div>
                <Button
                  variant="outline"
                  onClick={() => consultaMut.mutate()}
                  disabled={!canConsultar || consultaMut.isPending}
                  data-testid="recharge-consultar-button"
                >
                  {consultaMut.isPending ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search className="mr-2" size={16} />}
                  Consultar saldo / deuda
                </Button>
              </div>
            )}

            {consultaResult && (
              <div
                className={`rounded-lg border p-3 text-sm ${
                  consultaResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"
                }`}
                data-testid="recharge-consulta-result"
              >
                <div className="font-medium mb-1">{consultaResult.message || (consultaResult.ok ? "Consulta exitosa" : "Sin datos")}</div>
                <pre className="text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(consultaResult.data, null, 2)}
                </pre>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Monto (Bs)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  placeholder="0,00"
                  data-testid="recharge-monto-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nombre del cliente (opcional)</Label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  data-testid="recharge-customer-input"
                />
              </div>
            </div>

            <Button
              className="w-full h-14 text-base"
              onClick={() => recargaMut.mutate()}
              disabled={!canRecargar || recargaMut.isPending}
              data-testid="recharge-submit-button"
            >
              {recargaMut.isPending ? <Loader2 className="animate-spin mr-2" size={18} /> : <Zap className="mr-2" size={18} />}
              Recargar {monto ? `· Bs ${formatBs(Number(monto))}` : ""}
            </Button>
          </>
        )}
      </Card>

      {comprobante && (
        <Card className="p-4 border-emerald-500/40" data-testid="recharge-comprobante">
          <div className="flex items-center gap-2 mb-3 no-print">
            {comprobante.status === "approved" ? (
              <CheckCircle2 className="text-emerald-500" size={22} />
            ) : (
              <XCircle className="text-rose-500" size={22} />
            )}
            <div className="font-bold text-lg">
              {comprobante.status === "approved" ? "Recarga aprobada" : "Recarga rechazada"}
            </div>
            <Badge variant="outline" className="ml-auto uppercase">{comprobante.status}</Badge>
          </div>

          {/* Selector de ancho de papel */}
          <div className="flex items-center gap-2 mb-3 no-print">
            <span className="text-xs text-muted-foreground">Papel:</span>
            <div className="inline-flex rounded-md border overflow-hidden">
              {["58mm", "80mm"].map((sz) => (
                <button
                  key={sz}
                  onClick={() => setPaperSize(sz)}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    paperSize === sz ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground"
                  }`}
                  data-testid={`recharge-paper-${sz.replace("mm", "")}`}
                >
                  {sz}
                </button>
              ))}
            </div>
          </div>

          {/* Comprobante térmico (área imprimible) */}
          <div
            id="ekiipago-print-area"
            className={`mx-auto bg-white text-black font-mono text-[12px] leading-tight p-3 rounded border ${
              paperSize === "80mm" ? "thermal-80 max-w-[300px]" : "thermal-58 max-w-[220px]"
            }`}
            data-testid="recharge-thermal-receipt"
          >
            <div className="text-center">
              <div className="font-bold text-base tracking-wide">CONNECT</div>
              <div className="text-[11px]">SISTEMA DE JUEGOS</div>
              <div className="font-bold text-[11px] mt-0.5">RECARGA DE SERVICIOS</div>
              <div className="text-[10px]">{user?.full_name || ""}</div>
              <div className="text-[10px]">{formatDate(comprobante.created_at)}</div>
            </div>
            <div className="border-t border-dashed border-black my-1.5" />
            <TRow k="OPERADORA" v={comprobante.provider_name} bold />
            <TRow k="Cuenta" v={comprobante.cuenta} testid="recharge-cuenta-value" />
            <div className="border-t border-dashed border-black my-1.5" />
            <div className="flex justify-between font-bold text-[15px]">
              <span>MONTO Bs</span>
              <span>{formatBs(comprobante.monto_bs)}</span>
            </div>
            <div className="border-t border-dashed border-black my-1.5" />
            <TRow k="N° Aprob." v={comprobante.approval_number || "—"} testid="recharge-approval-number" />
            <TRow k="Referencia" v={comprobante.reference} />
            <TRow k="Estatus" v={comprobante.status === "approved" ? "APROBADA" : String(comprobante.status).toUpperCase()} />
            <div className="border-t border-dashed border-black my-1.5" />
            <div className="text-center text-[10px] mt-1">
              <div>{comprobante.status === "approved" ? "Recarga procesada" : "Recarga NO procesada"}</div>
              <div>Conserve este comprobante</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4 no-print">
            <Button variant="outline" onClick={printManual} data-testid="recharge-print-button">
              <Printer size={16} className="mr-2" /> Imprimir ({isDesktopRawActive() || (getPrinterSettings().print_method === "serial" && isWebSerialAvailable()) ? "directa" : isDesktopApp() ? "sistema" : "navegador"})
            </Button>
            <Button
              variant="outline"
              onClick={printBluetooth}
              disabled={btPrinting || !isWebBluetoothAvailable()}
              data-testid="recharge-print-bluetooth-button"
            >
              {btPrinting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Bluetooth size={16} className="mr-2" />}
              Imprimir Bluetooth
            </Button>
            <Button variant="ghost" onClick={resetForm} data-testid="recharge-new-button">
              Nueva recarga
            </Button>
          </div>
        </Card>
      )}
      </fieldset>
    </div>
  );
}

function TRow({ k, v, testid, bold }) {
  return (
    <div className="flex justify-between gap-3">
      <span>{k}</span>
      <span className={`text-right ${bold ? "font-bold" : ""}`} data-testid={testid}>{v}</span>
    </div>
  );
}
